/**
 * Account identity and CMMD account context.
 *
 * @module
 */
import { CmmdApiError, cmmdApiClient } from "../../environments/cmmdApi";
import { cn } from "../../lib/utils";
import { PersonAvatar } from "../identity/PersonAvatar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  resolveCmmdProfilePermissions,
  resolveCmmdProfileSummary,
} from "./ProfileSettingsPanel.logic";
import { maskConnectedAccountLabel } from "../../lib/connectedAccountEmail";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { useCurrentForgeWorkspaceState } from "../../workspace/currentForgeWorkspaceStore";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  CircleGaugeIcon,
  EyeIcon,
  EyeOffIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useMemo, useState } from "react";

function resolveProfileErrorMessage(error: unknown): string {
  if (error instanceof CmmdApiError && error.isAuth()) {
    return "CMMD authentication is required. Reconnect CMMD to continue.";
  }
  return error instanceof Error ? error.message : "Profile context is unavailable.";
}

type CmmdProfileSummary = NonNullable<ReturnType<typeof resolveCmmdProfileSummary>>;

type CmmdProfilePermissions = NonNullable<ReturnType<typeof resolveCmmdProfilePermissions>>;

function ProfileSummarySkeleton() {
  return (
    <div className="px-4 py-6 sm:px-5">
      <div className="flex items-center gap-4">
        <Skeleton className="mx-auto size-20 rounded-full" />
        <div className="mx-auto min-w-0 flex-1 space-y-2">
          <Skeleton className="mx-auto h-4 w-32" />
          <Skeleton className="mx-auto h-3 w-44" />
        </div>
      </div>
    </div>
  );
}

function ProfileLoadFailure({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-5 sm:px-5">
      <div className="space-y-1">
        <p className="text-[13px] font-medium text-foreground">Unable to load profile</p>
        <p className="text-xs text-muted-foreground">{message}</p>
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} className="shrink-0">
        Retry
      </Button>
    </div>
  );
}

interface ProfileIdentityCardProps {
  readonly summary: CmmdProfileSummary;
  readonly permissions: CmmdProfilePermissions | null;
  readonly isPermissionsLoading: boolean;
  readonly onViewUsage: () => void;
}

/** Avatar, name, handle, admin badge, and the way through to usage. */

function ProfileIdentityCard({
  summary,
  permissions,
  isPermissionsLoading,
  onViewUsage,
}: ProfileIdentityCardProps) {
  return (
    <div className="px-4 py-8 sm:px-5">
      <div className="flex flex-col items-center text-center">
        <PersonAvatar
          name={summary.displayName}
          avatarUrl={summary.avatarUrl}
          className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted text-2xl font-semibold text-foreground"
        />

        <div className="mt-4 min-w-0">
          <div className="truncate text-2xl font-semibold text-foreground">
            {summary.displayName}
          </div>
          <div className="mt-1 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>@{summary.username}</span>
            {isPermissionsLoading ? (
              <Skeleton className="h-4 w-12 rounded" />
            ) : permissions?.isAdmin ? (
              <span className="inline-flex items-center gap-1 rounded border border-warning-border bg-warning-surface px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-warning-foreground uppercase">
                <ShieldCheckIcon className="size-2.5" />
                Admin
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Token activity lives on the usage page, which merges CMMD's
          record with the Claude Code and Codex transcripts every
          connected environment reports. */}
      <div className="mt-8 flex justify-center">
        <Button size="sm" variant="outline" onClick={onViewUsage} className="gap-1.5">
          <CircleGaugeIcon className="size-3.5" />
          View usage
        </Button>
      </div>

      {summary.warnings.length > 0 ? (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-warning-border bg-warning-surface px-3 py-2.5">
          <AlertCircleIcon className="mt-0.5 size-3.5 shrink-0 text-warning-foreground" />
          <p className="text-xs text-warning-foreground">{summary.warnings.join(" ")}</p>
        </div>
      ) : null}
    </div>
  );
}

function ProfileContactSection({ summary }: { readonly summary: CmmdProfileSummary }) {
  const [showEmail, setShowEmail] = useState(false);
  return (
    <SettingsSection title="Contact">
      <SettingsRow
        title="Email"
        description="Your CMMD account email address."
        control={
          <span className="inline-flex items-center gap-1.5">
            <span className="font-mono text-xs text-foreground/80">
              {showEmail ? summary.email : maskConnectedAccountLabel(summary.email)}
            </span>
            <button
              type="button"
              onClick={() => setShowEmail((v) => !v)}
              className="text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              aria-label={showEmail ? "Hide email" : "Show email"}
            >
              {showEmail ? <EyeOffIcon className="size-3" /> : <EyeIcon className="size-3" />}
            </button>
          </span>
        }
      />
      <SettingsRow
        title="Username"
        description="Your unique handle on CMMD."
        control={<span className="font-mono text-xs text-foreground/80">@{summary.username}</span>}
      />
    </SettingsSection>
  );
}

export function ProfileSection() {
  const navigate = useNavigate();

  const profileQuery = useQuery({
    queryKey: ["cmmd", "profile"],
    queryFn: () => cmmdApiClient.getProfile(),
    staleTime: 60_000,
  });
  // Reactive, not the plain getter: ProfileSection can stay mounted across a
  // workspace switch, and a non-reactive read would leave the bootstrap
  // query pinned to the workspace active when it first rendered (CodeRabbit,
  // d30f19a review).
  const { workspaceId: currentWorkspaceId } = useCurrentForgeWorkspaceState();
  const bootstrapQuery = useQuery({
    queryKey: ["cmmd", "bootstrap", currentWorkspaceId ?? "none"],
    queryFn: () =>
      cmmdApiClient.getBootstrap(
        currentWorkspaceId !== null ? { organizationId: currentWorkspaceId } : undefined,
      ),
    // Bootstrap is a real CMMD network call — cache for 5 min so within-session
    // navigation (e.g. switching settings panels) never re-fetches.
    staleTime: 5 * 60_000,
  });

  const summary = useMemo(
    () => (profileQuery.data ? resolveCmmdProfileSummary(profileQuery.data) : null),
    [profileQuery.data],
  );
  const permissions = useMemo(
    () => (bootstrapQuery.data ? resolveCmmdProfilePermissions(bootstrapQuery.data) : null),
    [bootstrapQuery.data],
  );
  const isFetching = profileQuery.isFetching || bootstrapQuery.isFetching;

  const handleRefresh = () => {
    void profileQuery.refetch();
    void bootstrapQuery.refetch();
  };

  const renderBody = () => {
    if (profileQuery.isLoading) return <ProfileSummarySkeleton />;
    if (profileQuery.isError) {
      return (
        <ProfileLoadFailure
          message={resolveProfileErrorMessage(profileQuery.error)}
          onRetry={handleRefresh}
        />
      );
    }
    if (summary === null) {
      return (
        <div className="px-4 py-5 sm:px-5">
          <p className="text-xs text-muted-foreground">
            No profile context returned. Refresh to try again.
          </p>
        </div>
      );
    }
    return (
      <ProfileIdentityCard
        summary={summary}
        permissions={permissions}
        isPermissionsLoading={bootstrapQuery.isLoading}
        onViewUsage={() => void navigate({ to: "/usage" })}
      />
    );
  };

  return (
    <>
      {/* The `#profile` anchor belongs here, on the section that always
          renders. It was on the Contact section, which only appears once the
          CMMD profile query resolves -- so /settings/profile redirected to a
          hash that does not exist while the page is still loading, which is
          exactly when someone following that redirect arrives. */}
      <SettingsSection
        id="profile"
        title="Profile"
        headerAction={
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCwIcon className={cn("size-3.5", isFetching ? "animate-spin" : "")} />
            Refresh
          </Button>
        }
      >
        {renderBody()}
      </SettingsSection>

      {summary === null ? null : <ProfileContactSection summary={summary} />}
    </>
  );
}
